/**
 * Day 94 P8.1 — pure predicate-builder for the four Buy Score sidebar filters
 * wired this day: max_competition, min_price, max_price, min_velocity.
 *
 * Maps the locked sidebar filter values + grid mode → server-side predicate
 * descriptors. BuyingSession.getRows applies the descriptors to the Supabase
 * query (single-channel: product_profitability with a products!inner embed;
 * cross-channel: the products table directly). Pure — no React, no Supabase
 * (mirrors scenarioCleanup.ts); unit-tested in src/test/day94FilterPredicates.test.ts.
 *
 * Locked decisions (Day 94 Phase 0):
 *   B   min_velocity → product_profitability.monthly_sales (single) /
 *       products.amazon_monthly_sales (all). The grid rides the 2-tier
 *       monthly_sales from calculate_profitability, NOT forecast_restock CF-49.
 *   C+G price → product_profitability.sale_price (single) /
 *       products.amazon_buy_box_price (all). All four go server-side in BOTH
 *       modes (cross-channel uses product-level columns so the predicate runs
 *       pre-pagination with an exact count, unlike the page-local roi/margin/
 *       profit client-side filters which stay out of Day-94 scope).
 *   D   Direction-aware NULL — MAX filters INCLUDE NULL (.or '<col>.is.null');
 *       MIN filters EXCLUDE NULL (plain .gte), parity with the 3 shipped mins.
 *   carry-lock 1  MAX guards accept 0 (e.g. max_competition=0 = "no sellers");
 *       MIN guards require > 0 (a 0 floor is a no-op, matching shipped mins).
 *
 * max_returns_rate (H.1) is intentionally absent — pulled from Day 94 (no
 * per-product returns column exists). min_demand_score / min_bsr are Day 95.
 */

type PredicateOp = 'gte' | 'lte';
export type PredicateMode = 'single' | 'all';

export interface PredicateDescriptor {
  /** Source filter key — provenance for tests/logging; not used in the query. */
  filterKey: string;
  /** Bare DB column name (no table prefix; referencedTable carries the table). */
  column: string;
  /** 'gte' for MIN filters, 'lte' for MAX filters. */
  op: PredicateOp;
  /** Parsed numeric threshold. */
  value: number;
  /** Set for single-channel products-side columns (embedded under product_profitability). */
  referencedTable?: 'products';
  /** true → caller emits `.or('<col>.<op>.<value>,<col>.is.null')` (MAX); false → plain `.<op>`. */
  includeNull: boolean;
}

type FilterDirection = 'min' | 'max';

interface FilterSpec {
  dir: FilterDirection;
  single: { column: string; referencedTable?: 'products' };
  all: { column: string; referencedTable?: 'products' };
}

/**
 * The four Day-94 filters and their per-mode column targets. Existing
 * roi/margin/profit filters stay inline in getRows (not refactored here).
 */
const DAY94_FILTER_SPECS: Record<string, FilterSpec> = {
  max_competition: {
    dir: 'max',
    single: { column: 'amazon_seller_count', referencedTable: 'products' },
    all: { column: 'amazon_seller_count' },
  },
  min_price: {
    dir: 'min',
    single: { column: 'sale_price' },
    all: { column: 'amazon_buy_box_price' },
  },
  max_price: {
    dir: 'max',
    single: { column: 'sale_price' },
    all: { column: 'amazon_buy_box_price' },
  },
  min_velocity: {
    dir: 'min',
    single: { column: 'monthly_sales' },
    all: { column: 'amazon_monthly_sales' },
  },
};

/**
 * Build server-side predicate descriptors for the four Day-94 filters from the
 * current sidebar filter values and grid mode. Empty / non-numeric values and
 * (for MIN filters) non-positive values yield no descriptor.
 */
export function buildBuyingSessionPredicates(
  filters: Record<string, string | undefined>,
  mode: PredicateMode
): PredicateDescriptor[] {
  const descriptors: PredicateDescriptor[] = [];
  for (const [filterKey, spec] of Object.entries(DAY94_FILTER_SPECS)) {
    const raw = filters[filterKey];
    if (raw == null || raw === '') continue;
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) continue;
    // carry-lock 1: MAX accepts 0; MIN requires > 0 (parity with shipped mins).
    if (spec.dir === 'min' && !(value > 0)) continue;
    const target = mode === 'single' ? spec.single : spec.all;
    descriptors.push({
      filterKey,
      column: target.column,
      op: spec.dir === 'min' ? 'gte' : 'lte',
      value,
      referencedTable: target.referencedTable,
      includeNull: spec.dir === 'max', // decision D
    });
  }
  return descriptors;
}

/**
 * Day 100a — pure embed-spec builder for the three cross-channel ('all' mode)
 * profitability MIN filters: min_roi_pct / min_margin_pct / min_profit_unit.
 *
 * Each returned spec becomes an ALIASED `product_profitability!inner(<column>)`
 * embed on the BuyingSession.getRows products query, filtered with
 * `.eq('<alias>.distributor_id', …)` + `.gte('<alias>.<column>', min)` BEFORE
 * `.range` — so All-Channels mode filters the full dataset pre-pagination and
 * count:'exact' is the true filtered total (replacing the former page-local
 * client-side Math.max-after-.range block).
 *
 * Three INDEPENDENT aliased inner embeds preserve the original independent-per-
 * metric MAX semantics: best-roi-channel >= R AND best-margin-channel >= M AND
 * best-profit-channel >= P, each metric's max from a possibly-different channel
 * (an `!inner` existence join on metric M ⇔ ∃ a channel with M >= threshold ⇔
 * MAX(M) >= threshold). Verified equivalent on the live DB at the Day-100a
 * weigh-in (discriminating case independent=856 vs same-channel=855 → count=856;
 * the trivial threshold → count = distinct products, NOT the 6× embed cardinality
 * — count:'exact' counts deduplicated top-level products).
 *
 * MIN-guard parity with buildBuyingSessionPredicates carry-lock 1: empty /
 * non-numeric / non-positive thresholds yield no embed (matches the prior inline
 * `min && parseFloat(min) > 0` guard). Pure — no React, no Supabase. Unit-tested
 * in src/test/day100aCrossChannelEmbeds.test.ts.
 */
export interface CrossChannelProfitEmbed {
  /** Source filter key — provenance for tests/logging; not used in the query. */
  filterKey: string;
  /** Unique embed alias disambiguating the repeated product_profitability embed. */
  alias: string;
  /** product_profitability metric column embedded + filtered for this spec. */
  column: string;
  /** Parsed numeric MIN threshold (caller emits `.gte('<alias>.<column>', min)`). */
  min: number;
}

const CROSS_CHANNEL_PROFIT_EMBED_SPECS: ReadonlyArray<{
  filterKey: string;
  alias: string;
  column: string;
}> = [
  { filterKey: 'min_roi_pct', alias: 'pp_roi', column: 'roi_pct' },
  { filterKey: 'min_margin_pct', alias: 'pp_margin', column: 'margin_pct' },
  { filterKey: 'min_profit_unit', alias: 'pp_profit', column: 'profit_per_unit' },
];

export function buildCrossChannelProfitEmbeds(
  filters: Record<string, string | undefined>
): CrossChannelProfitEmbed[] {
  const embeds: CrossChannelProfitEmbed[] = [];
  for (const spec of CROSS_CHANNEL_PROFIT_EMBED_SPECS) {
    const raw = filters[spec.filterKey];
    if (raw == null || raw === '') continue;
    const min = parseFloat(raw);
    if (!Number.isFinite(min)) continue; // NaN / non-numeric
    if (!(min > 0)) continue; // non-positive (MIN parity; matches the prior > 0 guard)
    embeds.push({ filterKey: spec.filterKey, alias: spec.alias, column: spec.column, min });
  }
  return embeds;
}

/**
 * Day 95 P8.2 (H.2) — text→numeric normalization for the min_demand_score
 * sidebar filter. products.amazon_demand_rating is a categorical TEXT column;
 * PROD value set (Day 95 Phase 0): LOW, MEDIUM, UNAVAILABLE (no-data sentinel),
 * NULL. No HIGH in data today — HIGH is mapped for forward-compat (filters with
 * no code change when Algopix emits it).
 *
 * Canonical ordinal scale: LOW=1, MEDIUM=2, HIGH=3. UNAVAILABLE + NULL are
 * no-data — never in any returned set, so a MIN filter excludes them (decision
 * D parity with the Day-94 mins; .in() excludes NULL/non-members natively).
 *
 * Returns:
 *   null       → no filter (empty / non-numeric / <= 0 threshold; MIN parity
 *                with buildBuyingSessionPredicates carry-lock 1).
 *   string[]   → labels with ordinal >= threshold. MAY be empty when the
 *                threshold exceeds the max ordinal (e.g. 4+); the caller applies
 *                a never-match sentinel so an out-of-scale floor returns ZERO
 *                rows (not every row) without an empty .in([]).
 *
 * Pure — no React, no Supabase (mirrors buildBuyingSessionPredicates).
 * Unit-tested in src/test/day95FilterPredicates.test.ts.
 */
export const DEMAND_RATING_ORDINAL: Record<string, number> = {
  LOW: 1,
  MEDIUM: 2,
  HIGH: 3,
};

/** Sentinel applied when the qualifying set is empty (out-of-scale threshold):
 *  matches no real rating value, so the filter returns zero rows. */
export const DEMAND_NO_MATCH_SENTINEL = '__no_match__';

export function normalizeDemandThreshold(raw: string | number | undefined | null): string[] | null {
  if (raw == null || raw === '') return null;
  const threshold = typeof raw === 'number' ? raw : parseFloat(raw);
  if (!Number.isFinite(threshold) || threshold <= 0) return null; // MIN parity
  return Object.entries(DEMAND_RATING_ORDINAL)
    .filter(([, ordinal]) => ordinal >= threshold)
    .map(([label]) => label);
}
